import { cn } from "@aircall/ds";
import {
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  forwardRef,
} from "react";

import { formatSecondsToDuration } from "@/helpers/date/date.helpers";

export interface DurationProps extends ComponentPropsWithoutRef<"span"> {
  /** Duration to format, in seconds. */
  seconds: number;
  /**
   * Whether the formatted duration includes every unit (`d h min s`) or skips
   * some depending on the value. Defaults to `true`.
   */
  extended?: boolean;
}

/**
 * Structureless leaf that renders a seconds value as a human-readable duration
 * (e.g. `1min 30s`). Mirrors the sibling `Line`/`UserName` blocks: a single
 * `forwardRef` span with a `data-slot`, `cn`-merged className, and full
 * rest-prop spread — no render-prop child, no cva (neither is wired in this
 * app).
 *
 * ```tsx
 * <Duration seconds={90} />            // 1min 30s
 * <Duration seconds={86400} extended={false} /> // 1d 0h
 * ```
 */
export const Duration = forwardRef<HTMLSpanElement, Duration.Props>(
  (
    componentProps: Duration.Props,
    forwardRef: ForwardedRef<HTMLSpanElement>
  ) => {
    const { seconds, extended = true, className, ...props } = componentProps;

    return (
      <span
        ref={forwardRef}
        data-slot="duration"
        className={cn("text-sm", className)}
        {...props}
      >
        {formatSecondsToDuration(seconds, extended)}
      </span>
    );
  }
);
Duration.displayName = "Duration";

export namespace Duration {
  export type Props = DurationProps;
}
